import React, { useState, useRef, useEffect , useContext} from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase/fire";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { setDoc, doc, getDocs, collection, getDoc, Timestamp, query, where } from "firebase/firestore";
import { FaUser, FaEnvelope, FaLock, FaPhoneAlt, FaAddressCard, FaCamera, FaArrowCircleLeft, FaEye, FaEyeSlash, FaFileContract, FaCheckCircle, FaTimes } from 'react-icons/fa';
import ReCAPTCHA from "react-google-recaptcha";
import LogoComponent from "./LogoComponent";
import Modal from "./Modal";
import { LogoContext } from "./LogoContext";
import { notifyAdminNewSignup } from "../utils/adminNotification";

export default function MemberSignUp() {
  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    street: "",
    city: "",
    pincode: "",
    photo: null,
    agreeToDisclaimer: false,
    emergencyContact: "",
    emergencyContactRelation: "",
    honeypot: ""
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const [photoCaptured, setPhotoCaptured] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [isCameraModalOpen, setIsCameraModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const recaptchaRef = useRef(null);
  const formStartTime = useRef(Date.now());
  const interactionCount = useRef(0);
  
  const navigate = useNavigate();
  const [classSchedule, setClassSchedule] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [disclaimerFile, setDisclaimerFile] = useState("");
  const [logoUrl, setLogoUrl] = useContext(LogoContext);

  
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) {
      //console.error("RECAPTCHA_SITE_KEY is missing! Add it to .env file");
      setError("Configuration error. Please contact support.");
    }
  }, [RECAPTCHA_SITE_KEY]);

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  const trackInteraction = () => {
    interactionCount.current += 1;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    const fetchClassSchedule = async () => {
      try {
        const scheduleSnapshot = await getDocs(collection(db, "classSchedule"));
        const scheduleList = scheduleSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setClassSchedule(scheduleList);
      } catch (error) {
        console.error("Error fetching class schedule: ", error);
      }
    };
    fetchClassSchedule();
  }, []);

  useEffect(() => {
    const fetchDisclaimer = async () => {
      try {
        const docRef = doc(db, "agreements", "member");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setDisclaimerFile({
            name: data.fileName,
            type: data.fileType,
            content: data.fileData,
          });
        } else {
          console.error("No disclaimer found in database.");
        }
      } catch (error) {
        console.error("Error fetching disclaimer: ", error);
      }
    };

    fetchDisclaimer();
  }, []);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        if (!value) {
          error = "First Name is required";
        } else if (value.length < 2) {
          error = "Name must be at least 2 characters";
        } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          error = "Name should contain only letters";
        }
        break;
      case "lastName":
        if (!value) {
          error = "Last Name is required";
        } else if (value.length < 2) {
          error = "Last name must be at least 2 characters";
        } else if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          error = "Last name should contain only letters";
        }
        break;
      case "email":
        if (!value) {
          error = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          error = "Invalid email format";
        }
        break;
      case "password":
        if (!value) {
          error = "Password is required";
        } else if (value.length < 6) {
          error = "Password should be at least 6 characters";
        }
        break;
      case "confirmPassword":
        if (!value) {
          error = "Please confirm your password";
        } else if (value !== formData.password) {
          error = "Passwords do not match";
        }
        break;
      case "phone":
        if (!value) {
          error = "Phone number is required";
        } else if (!/^[0-9]{8,15}$/.test(value)) {
          error = "Invalid phone number";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    
    trackInteraction();
    
    setFormData({ ...formData, [name]: newValue });

    const error = validateField(name, newValue);
    setErrors({ ...errors, [name]: error });
  };

  const handleCapture = () => {
    if (!isCapturing) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((stream) => {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          setIsCapturing(true);
        })
        .catch((err) => setError("Error accessing camera: " + err.message));
    } else {
      const canvas = canvasRef.current;
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      const photo = canvas.toDataURL("image/png");
      setFormData({ ...formData, photo });
      setPhotoCaptured(true);
      setIsCapturing(false);
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
  };

  const openCameraModal = () => {
    setIsCameraModalOpen(true);
    setTimeout(() => {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
            setIsCapturing(true);
          }
        })
        .catch((err) => setError("Error accessing camera: " + err.message));
    }, 100);
  };

  const closeCameraModal = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
    setIsCameraModalOpen(false);
    setIsCapturing(false);
  };

  const cancelCameraCapture = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
    setFormData({ ...formData, photo: null });
    setPhotoCaptured(false);
    setIsCameraModalOpen(false);
    setIsCapturing(false);
  };

  const handleRetake = () => {
    setFormData({ ...formData, photo: null });
    setPhotoCaptured(false);
    setTimeout(() => {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
            setIsCapturing(true);
          }
        })
        .catch((err) => setError("Error accessing camera: " + err.message));
    }, 100);
  };

  const confirmPhoto = () => {
    closeCameraModal();
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    setErrors({ ...errors, recaptcha: "" });
  };

  const onRecaptchaExpired = () => {
    setRecaptchaToken(null);
    setErrors({ ...errors, recaptcha: "reCAPTCHA expired. Please verify again." });
  };

  const openDisclaimerModal = () => {
    setIsDisclaimerModalOpen(true);
  };

  const closeDisclaimerModal = () => {
    setIsDisclaimerModalOpen(false);
  };

  const acceptDisclaimer = () => {
    setFormData({ ...formData, agreeToDisclaimer: true });
    closeDisclaimerModal();
  };

  const declineDisclaimer = () => {
    setFormData({ ...formData, agreeToDisclaimer: false });
    closeDisclaimerModal();
  };

  const clearForm = () => {
    setFormData({
      name: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phone: "",
      street: "",
      city: "",
      pincode: "",
      photo: null,
      agreeToDisclaimer: false,
      emergencyContact: "",
      emergencyContactRelation: "",
      honeypot: ""
    });
    setErrors({});
    setPhotoCaptured(false);
    setError("");
    setSuccess("");
    setRecaptchaToken(null);
    setShowPassword(false);
    setShowConfirmPassword(false);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleSubmit = async (e) => {
    // prevent concurrent submissions
    if (isSubmitting) return;

    setErrors({});
    e.preventDefault();

    // ========== SPAM PROTECTION CHECKS ==========
    
    // 1. Honeypot check
    if (formData.honeypot) {
      showModal("Registration failed. Please try again.");
      return;
    }

    // 2. Time-based check
    const timeTaken = (Date.now() - formStartTime.current) / 1000;
    if (timeTaken < 5) {
      showModal("Please take your time filling out the form.");
      return;
    }

    // 3. Interaction check
    if (interactionCount.current < 5) {
      showModal("Please complete all form fields.");
      return;
    }

    //4. reCAPTCHA verification
    if (!recaptchaToken) {
      setErrors({ ...errors, recaptcha: "Please complete the reCAPTCHA verification" });
      showModal("Please verify that you're not a robot by completing the reCAPTCHA.");
      return;
    }

    // 5. Validate form fields
    const formErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'honeypot' && key !== 'street' && key !== 'city' && key !== 'pincode' && key !== 'emergencyContact' && key !== 'emergencyContactRelation') {
        const error = validateField(key, formData[key]);
        if (error) formErrors[key] = error;
      }
    });

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    if (!formData.agreeToDisclaimer) {
      setErrors({ ...errors, disclaimer: "Please accept the disclaimer before signup" });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prevent duplicate member documents by email (reject signup if exists)
      const normalizedEmail = formData.email.trim().toLowerCase();
      const membersQuery = query(collection(db, 'members'), where('email', '==', normalizedEmail));
      const existingSnapshot = await getDocs(membersQuery);
      if (existingSnapshot.docs.length > 0) {
        showModal("This email is already registered. Please log in instead.");
        if (recaptchaRef.current) recaptchaRef.current.reset();
        setRecaptchaToken(null);
        setIsSubmitting(false);
        return;
      }

      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      const user = userCredential.user;
      
      await sendEmailVerification(user);
      
      // Combine address fields
      const fullAddress = [formData.street, formData.city, formData.pincode].filter(Boolean).join(', ');
      
      await setDoc(doc(db, "members", user.uid), {
        name: formData.name,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: fullAddress,
        photo: formData.photo,
        member: true,
        isAdmin: false,
        isCoach: false,
        status: "Pending",
        membershipStartDate: null,
        membershipEndDate: null,
        classesRemaining: 0,
        totalClassesAttended: 0,
        weeklyClassesAttended: 0,
        freeTrailRemaining: 0,
        freeTrailClasses: 0,
        lastWeekReset: null,
        lastAttendanceDate: null,
        emergencyContact: formData.emergencyContact,
        emergencyContactRelation: formData.emergencyContactRelation,
        signedAgreement: disclaimerFile.content,
        agreementDate: Timestamp.now(),
        recaptchaVerified: true,
        registrationDate: Timestamp.now()
      });

      // Send email notification to admin immediately after signup
      try {
        const emailResult = await notifyAdminNewSignup({
          name: formData.name,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone
        });
        
        if (emailResult.success) {
          console.log('Admin notified of new signup via email');
        } else {
          console.warn('Failed to notify admin:', emailResult.error);
        }
      } catch (emailError) {
        // Don't block signup if email notification fails
        console.error('Error notifying admin:', emailError);
      }

      clearForm();
      
      showModal(
        <div className="text-sm">
          <div className="mb-4 pb-3 border-b border-gray-200">
            <div className="flex items-center gap-2 text-blue-600 font-medium">
              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
              Email verification required
            </div>
          </div>

          <div className="space-y-3">
            <div className="border-l-4 border-blue-500 pl-3 py-2">
              <div className="font-semibold text-gray-900">Check your email</div>
              <div className="text-gray-600 text-xs mt-1">
                Verify your account using the link we sent you
              </div>
            </div>

            <div className="border-l-4 border-purple-500 pl-3 py-2">
              <div className="font-semibold text-gray-900">Check spam/junk folder</div>
              <div className="text-gray-600 text-xs mt-1">
                The verification email might be in your spam folder
              </div>
            </div>

            <div className="border-l-4 border-amber-500 pl-3 py-2">
              <div className="font-semibold text-gray-900">Admin approval pending</div>
              <div className="text-gray-600 text-xs mt-1">
                Your registration is awaiting approval
              </div>
            </div>
          </div>
        </div>
      );
      
    } catch (error) {
      if (error.code === "auth/email-already-in-use" ||
        (error.error && error.error.message === "EMAIL_EXISTS")) {
        showModal("This email is already registered. Please log in instead.");
      } else {
        showModal(error.message || "An error occurred during sign up. Please try again.");
      }
      
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaToken(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render reCAPTCHA if key is missing
  if (!RECAPTCHA_SITE_KEY) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center text-red-600">
          <p className="text-xl font-bold">Configuration Error</p>
          <p className="mt-2">Please contact support</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-lg">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-700 text-sm font-medium mb-5 transition-colors">
          <FaArrowCircleLeft size={15} />
          Back
        </Link>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 px-8 py-8 sm:px-10">
          {/* Logo */}
          <div className="flex justify-center mb-5">
            <img src={logoUrl} className="h-20 sm:h-24 object-contain" alt="Logo" />
          </div>

          <h2 className="text-base font-bold text-gray-800 tracking-wider uppercase text-center mb-7">New Member Registration</h2>

          <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false">
            {/* Honeypot */}
            <input type="text" name="honeypot" value={formData.honeypot} onChange={handleChange} style={{ position: 'absolute', left: '-9999px' }} tabIndex="-1" autoComplete="off" aria-hidden="true" />

            {/* Photo */}
            <div className="flex flex-col items-center mb-1">
              <div
                onClick={openCameraModal}
                className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden border-2 border-slate-200 cursor-pointer hover:border-indigo-400 transition-colors shadow-sm mb-2"
              >
                {formData.photo ? (
                  <img src={formData.photo} className="w-full h-full object-cover" alt="Profile" />
                ) : (
                  <FaCamera className="text-slate-400 text-xl" />
                )}
              </div>
              <p className={`text-xs font-medium flex items-center gap-1 ${photoCaptured ? 'text-emerald-600' : 'text-slate-400'}`}>
                {photoCaptured ? <><FaCheckCircle size={11} /> Photo captured</> : 'Click to add photo'}
              </p>
            </div>
            <canvas ref={canvasRef} className="hidden" />

            {/* Personal Information */}
            <div>
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Personal Information</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none" />
                    <input
                      type="text" name="name" value={formData.name} onChange={handleChange}
                      placeholder="First Name" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                      className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                    />
                  </div>
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <div className="relative">
                    <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none" />
                    <input
                      type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                      placeholder="Last Name" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                      className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                    />
                  </div>
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div className="relative mb-3">
                <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none" />
                <input
                  type="email" name="email" value={formData.email} onChange={handleChange}
                  placeholder="Email" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                  className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <div className="relative">
                    <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none" />
                    <input
                      type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange}
                      placeholder="Password" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                      className="w-full pl-8 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                    />
                    <button type="button" onClick={togglePasswordVisibility} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 focus:outline-none" tabIndex="-1">
                      {showPassword ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                </div>
                <div>
                  <div className="relative">
                    <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
                      placeholder="Confirm Password" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                      className="w-full pl-8 pr-9 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                    />
                    <button type="button" onClick={toggleConfirmPasswordVisibility} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 focus:outline-none" tabIndex="-1">
                      {showConfirmPassword ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                    </button>
                  </div>
                  {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
                </div>
              </div>

              <div className="relative">
                <FaPhoneAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none" />
                <input
                  type="tel" name="phone" value={formData.phone} onChange={handleChange}
                  placeholder="Phone Number" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                  className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>

            {/* Address */}
            <div className="border-t border-gray-100 pt-5">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                Address <span className="font-normal normal-case text-slate-300">(optional)</span>
              </p>
              <div className="grid grid-cols-3 gap-2">
                <input type="text" name="street" value={formData.street} onChange={handleChange} placeholder="Street" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition" />
                <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="City" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition" />
                <input type="text" name="pincode" value={formData.pincode} onChange={handleChange} placeholder="Postcode" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition" />
              </div>
            </div>

            {/* Emergency Contact */}
            <div className="border-t border-gray-100 pt-5">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                Emergency Contact <span className="font-normal normal-case text-slate-300">(optional)</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <FaPhoneAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none" />
                  <input type="tel" name="emergencyContact" value={formData.emergencyContact} onChange={handleChange} placeholder="Phone Number" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                    className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition" />
                </div>
                <div className="relative">
                  <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs pointer-events-none" />
                  <input type="text" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleChange} placeholder="Relation" autoComplete="off" spellCheck="false" data-lpignore="true" data-form-type="other"
                    className="w-full pl-8 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition" />
                </div>
              </div>
            </div>

            {/* Terms */}
            <div className="border-t border-gray-100 pt-5">
              <button type="button" onClick={openDisclaimerModal} className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 text-xs font-semibold mb-3 transition-colors">
                <FaFileContract size={12} /> Read Terms &amp; Conditions
              </button>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" name="agreeToDisclaimer" checked={formData.agreeToDisclaimer} onChange={handleChange} className="w-4 h-4 rounded accent-indigo-600 cursor-pointer" />
                <span className="text-sm text-gray-600">I have read and agree to the Terms &amp; Conditions</span>
              </label>
              {errors.disclaimer && <p className="text-red-500 text-xs mt-1">{errors.disclaimer}</p>}
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center scale-90 origin-center">
              <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} onChange={onRecaptchaChange} onExpired={onRecaptchaExpired} theme="light" />
            </div>
            {errors.recaptcha && <p className="text-red-500 text-xs text-center">{errors.recaptcha}</p>}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-indigo-600 text-white py-3 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 ${isSubmitting ? 'opacity-40 cursor-not-allowed' : 'hover:bg-indigo-700 active:scale-[0.99]'}`}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>

          {error && <p className="mt-4 text-red-500 text-center text-sm">{error}</p>}
          {success && <p className="mt-4 text-emerald-600 text-center text-sm">{success}</p>}
        </div>
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="large" showBackButton={true} backAction={() => navigate("/")}>
        {modalMessage}
      </Modal>

      {/* Camera Capture Modal */}
      {isCameraModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between">
              <h3 className="text-sm font-bold tracking-wide uppercase">Capture Profile Photo</h3>
              <button onClick={cancelCameraCapture} className="text-white/50 hover:text-white transition-colors focus:outline-none">
                <FaTimes size={16} />
              </button>
            </div>
            <div className="p-5">
              {!photoCaptured ? (
                <div className="space-y-4">
                  <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-black">
                    <video ref={videoRef} className="w-full h-full object-cover" autoPlay playsInline />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const canvas = canvasRef.current;
                      canvas.width = videoRef.current.videoWidth;
                      canvas.height = videoRef.current.videoHeight;
                      const ctx = canvas.getContext("2d");
                      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
                      const photo = canvas.toDataURL("image/png");
                      setFormData({ ...formData, photo });
                      setPhotoCaptured(true);
                      if (videoRef.current && videoRef.current.srcObject) {
                        videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
                      }
                    }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2"
                  >
                    <FaCamera /> Capture Photo
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 border border-emerald-100 px-4 py-3 rounded-xl text-sm font-semibold">
                    <FaCheckCircle size={15} /> Photo captured successfully
                  </div>
                  <img src={formData.photo} className="w-full rounded-xl border border-gray-100" alt="Captured" />
                  <div className="flex gap-3">
                    <button type="button" onClick={handleRetake} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl font-semibold text-sm hover:bg-gray-50 transition">
                      Retake
                    </button>
                    <button type="button" onClick={confirmPhoto} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3 rounded-xl font-bold text-sm transition">
                      Confirm
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer Modal */}
      {isDisclaimerModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
            <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between shrink-0">
              <h3 className="text-sm font-bold tracking-wide uppercase">{disclaimerFile.name || "Terms & Conditions"}</h3>
              <button onClick={closeDisclaimerModal} className="text-white/50 hover:text-white transition-colors focus:outline-none">
                <FaTimes size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-hidden p-4 min-h-[350px]">
              <embed src={disclaimerFile.content} type={disclaimerFile.type} width="100%" height="100%" className="min-h-[350px]" />
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-100 shrink-0">
              <button onClick={declineDisclaimer} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 transition">
                Decline
              </button>
              <button onClick={acceptDisclaimer} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-xl text-sm font-bold transition">
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}